import React from 'react';
import Api from '../../api';
import { getCeDirectoryErrorCode } from './errors';

export const CE_DIRECTORY_FLAG = 'ce_courses_directory';
export const CE_DIRECTORY_AVAILABILITY_EVENT = 'tempstars:ce-directory-availability';

const PROBE_TIMEOUT_MS = 2500;
const availabilityByUser = new Map<string, Promise<boolean>>();
const refreshByUser = new Map<string, Promise<boolean>>();
let derivedFlagOwner = new WeakMap<object, string>();

type TempStarsTarget = {
  TempStars?: any;
  dispatchEvent?: (event: Event) => boolean;
};

type ClientFlagLoader = (signal: AbortSignal) => Promise<{
  ce_courses_directory?: unknown;
}>;

export function readExplicitCeDirectoryFlag(source: unknown = (window as any)?.TempStars): boolean | undefined {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return undefined;
  const value = (source as any)?.FeatureFlags?.[CE_DIRECTORY_FLAG];
  return typeof value === 'boolean' ? value : undefined;
}

function readCeDirectoryFlagForUser(
  userId: number | string,
  target: TempStarsTarget,
): boolean | undefined {
  const container = target.TempStars?.FeatureFlags;
  const value = readExplicitCeDirectoryFlag(target.TempStars);
  if (value === undefined || !container || typeof container !== 'object') return value;
  const owner = derivedFlagOwner.get(container);
  return owner === undefined || owner === String(userId) ? value : undefined;
}

export function publishCeDirectoryAvailability(
  userId: number | string,
  enabled: boolean,
  target: TempStarsTarget = window as any,
  derived = true,
): void {
  if (!target.TempStars || typeof target.TempStars !== 'object' || Array.isArray(target.TempStars)) {
    target.TempStars = {};
  }
  if (!target.TempStars.FeatureFlags
      || typeof target.TempStars.FeatureFlags !== 'object'
      || Array.isArray(target.TempStars.FeatureFlags)) {
    target.TempStars.FeatureFlags = {};
  }
  target.TempStars.FeatureFlags[CE_DIRECTORY_FLAG] = enabled;
  if (derived) {
    derivedFlagOwner.set(target.TempStars.FeatureFlags, String(userId));
  } else {
    derivedFlagOwner.delete(target.TempStars.FeatureFlags);
  }
  availabilityByUser.set(String(userId), Promise.resolve(enabled));
  if (typeof target.dispatchEvent === 'function' && typeof CustomEvent !== 'undefined') {
    target.dispatchEvent(new CustomEvent(CE_DIRECTORY_AVAILABILITY_EVENT, {
      detail: { userId: String(userId), enabled },
    }));
  }
}

function probeImpl(signal: AbortSignal) {
  return Api.getCeDirectoryListings({
    group: 'all',
    format: 'all',
    scope: 'province_state',
    page: 1,
  }, signal);
}

/**
 * Resolve one session-wide capability decision per global User ID. The exact
 * flag payload wins when available. Until the public flags endpoint exposes
 * this key, only CE_DIRECTORY_NOT_AVAILABLE means OFF; other probe failures
 * leave the feature ON with a directory-local retry state.
 */
export function getCeDirectoryAvailability(
  userId: number | string | null | undefined,
  probe: (signal: AbortSignal) => Promise<unknown> = probeImpl,
  target: TempStarsTarget = window as any,
): Promise<boolean> {
  if (userId == null || String(userId) === '') return Promise.resolve(false);
  const key = String(userId);
  const existing = availabilityByUser.get(key);
  if (existing) return existing;
  const explicit = readCeDirectoryFlagForUser(key, target);
  if (explicit !== undefined) {
    publishCeDirectoryAvailability(key, explicit, target, false);
    return Promise.resolve(explicit);
  }
  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const request = Promise.resolve()
    .then(() => probe(controller.signal))
    .then(() => true)
    .catch((error) => getCeDirectoryErrorCode(error) !== 'CE_DIRECTORY_NOT_AVAILABLE');
  const deadline = new Promise<boolean>(resolve => {
    timeout = setTimeout(() => {
      controller.abort();
      resolve(true);
    }, PROBE_TIMEOUT_MS);
  });
  const decision = Promise.race([request, deadline]).then(enabled => {
    if (timeout !== undefined) clearTimeout(timeout);
    publishCeDirectoryAvailability(key, enabled, target);
    return enabled;
  });
  availabilityByUser.set(key, decision);
  return decision;
}

/**
 * Re-read the authoritative public flag without disturbing a known session
 * decision when delivery is unavailable. Concurrent focus/page-show events
 * share one request per signed-in user.
 */
export function refreshCeDirectoryAvailability(
  userId: number | string | null | undefined,
  loadFlags: ClientFlagLoader,
  target: TempStarsTarget = window as any,
  timeoutMs: number = PROBE_TIMEOUT_MS,
): Promise<boolean> {
  if (userId == null || String(userId) === '') return Promise.resolve(false);
  const key = String(userId);
  const existing = refreshByUser.get(key);
  if (existing) return existing;

  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const request = Promise.resolve()
    .then(() => loadFlags(controller.signal))
    .then(flags => (typeof flags?.ce_courses_directory === 'boolean'
      ? flags.ce_courses_directory
      : undefined))
    .catch(() => undefined);
  const deadline = new Promise<undefined>(resolve => {
    timeout = setTimeout(() => {
      controller.abort();
      resolve(undefined);
    }, timeoutMs);
  });
  const refresh = Promise.race([request, deadline])
    .then(enabled => {
      if (timeout !== undefined) clearTimeout(timeout);
      if (typeof enabled === 'boolean') {
        publishCeDirectoryAvailability(key, enabled, target, false);
        return enabled;
      }
      return readCeDirectoryFlagForUser(key, target) ?? false;
    })
    .finally(() => {
      if (refreshByUser.get(key) === refresh) refreshByUser.delete(key);
    });
  refreshByUser.set(key, refresh);
  return refresh;
}

/** Refresh an open session after returning to its tab or restoring its page. */
export function installCeDirectoryAvailabilityRefresh(
  getUserId: () => number | string | null | undefined,
  loadFlags: ClientFlagLoader,
  target: TempStarsTarget & Pick<Window, 'addEventListener' | 'removeEventListener'> = window as any,
): () => void {
  const refresh = () => {
    void refreshCeDirectoryAvailability(getUserId(), loadFlags, target);
  };
  target.addEventListener('focus', refresh);
  target.addEventListener('pageshow', refresh);
  return () => {
    target.removeEventListener('focus', refresh);
    target.removeEventListener('pageshow', refresh);
  };
}

export function useCeDirectoryAvailability(
  userId: number | string | null | undefined,
): boolean | null {
  const [enabled, setEnabled] = React.useState<boolean | null>(() => {
    if (userId == null) return false;
    const explicit = readCeDirectoryFlagForUser(userId, window as any);
    return explicit === undefined ? null : explicit;
  });

  React.useEffect(() => {
    let active = true;
    void getCeDirectoryAvailability(userId).then(value => {
      if (active) setEnabled(value);
    });
    const handleAvailability = (event: Event) => {
      const detail = (event as CustomEvent)?.detail;
      if (detail?.userId === String(userId) && typeof detail?.enabled === 'boolean') {
        setEnabled(detail.enabled);
      }
    };
    window.addEventListener(CE_DIRECTORY_AVAILABILITY_EVENT, handleAvailability);
    return () => {
      active = false;
      window.removeEventListener(CE_DIRECTORY_AVAILABILITY_EVENT, handleAvailability);
    };
  }, [userId]);

  return enabled;
}

export function resetCeDirectoryAvailabilityForTests(): void {
  availabilityByUser.clear();
  refreshByUser.clear();
  derivedFlagOwner = new WeakMap<object, string>();
}
